import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaUserNurse, FaPaperclip, FaUserMd } from "react-icons/fa";
import {
  User,
  FileText,
  Eye,
  Pencil,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Calendar,
  Settings,
  Flag,
  Plus,
  Search,
  X,
} from "lucide-react";
import ClinicalNotesService from "../../../../Services/ClinicalNotesService";
import AddNoteModal from "../ClinicalNotes/AddNoteModal";
import AddNurseActionNoteModal from "./AddNurseActionNoteModal";
import NoteDetailsModal from "../ClinicalNotes/NoteDetailsModal";

const formatDueDatetime = (value) => {
  if (!value) return "12 Oct 2026, 04:10 pm";
  try {
    const d = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return value;
    const datePart = d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const timePart = d
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
    return `${datePart}, ${timePart}`;
  } catch {
    return value;
  }
};

const formatNurseNoteDateTime = (value) => {
  if (!value) return { date: "12 Oct 2026", time: "09:34 PM" };
  try {
    const d = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) {
      return { date: String(value), time: "" };
    }
    const date = d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return { date, time };
  } catch {
    return { date: String(value), time: "" };
  }
};

const formatDateOnly = (value) => {
  if (!value) return "";
  try {
    const d = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

const getStatusBadgeClass = (status) => {
  const s = (status || "").toUpperCase();
  if (s === "OPEN") {
    return "bg-[#dcfce7] text-[#16a34a]";
  }
  if (s === "IN_PROGRESS" || s === "IN PROGRESS") {
    return "bg-[#fef3c7] text-[#d97706]";
  }
  if (s === "COMPLETED" || s === "CLOSED") {
    return "bg-[#dbeafe] text-[#2563eb]";
  }
  return "bg-[#dcfce7] text-[#16a34a]";
};

const getPriorityBadgeClass = (priority) => {
  const p = (priority || "").toUpperCase();
  if (p === "HIGH" || p === "URGENT") {
    return "bg-[#fee2e2] text-[#ef4444]";
  }
  if (p === "MEDIUM") {
    return "bg-[#fef3c7] text-[#d97706]";
  }
  if (p === "LOW" || p === "ROUTINE") {
    return "bg-[#e0f2fe] text-[#0284c7]";
  }
  return "bg-[#fee2e2] text-[#ef4444]";
};

function SkeletonNote() {
  return (
    <div className="border border-[#e5eaf2] rounded-2xl p-6 bg-white animate-pulse space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-200" />
          <div className="h-4 w-32 bg-gray-200 rounded" />
          <div className="h-5 w-24 bg-gray-200 rounded-full" />
        </div>
        <div className="flex gap-2">
          <div className="w-9 h-9 bg-gray-200 rounded-xl" />
          <div className="w-9 h-9 bg-gray-200 rounded-xl" />
          <div className="w-9 h-9 bg-gray-200 rounded-xl" />
        </div>
      </div>
      <div className="h-4 w-1/4 bg-gray-200 rounded" />
      <div className="h-28 bg-gray-100 rounded-2xl" />
      <div className="h-32 bg-gray-100 rounded-2xl" />
    </div>
  );
}

function DoctorNoteCard({ note, onView, onEdit }) {
  const [expanded, setExpanded] = useState(true);

  const attachments = Array.isArray(note.attachments) ? note.attachments : [];

  const doctorName =
    note.recordedByName || note.createdBy || "Hariharan V";
  const noteType =
    note.role || note.noteTypeName || "Doctor Note";
  const noteText =
    note.noteText || note.content || "BP is high";

  const instructionText =
    note.nurseActionInstructions ||
    note.nurseAction?.actionInstructions ||
    note.nurseAction?.nurseActionInstructions ||
    note.instructions ||
    "Take vitals every 1 hour.";

  const dueDateTimeStr =
    note.dueDatetime ||
    note.nurseAction?.dueDatetime ||
    note.noteDatetime ||
    "2026-10-12 16:10:00";

  const taskStatus =
    note.status ||
    note.nurseAction?.status ||
    "OPEN";

  const priority =
    note.priority ||
    note.nurseAction?.priority ||
    "High";

  const defaultNursingUpdates = [
    {
      id: 1,
      nurseName: note.assignedNurseName || "Kaaviya",
      role: "Nurse",
      noteText: "Vitals taken. BP 150/90, Pulse 88, SpO2 98%. Patient stable.",
      date: "12 Oct 2026",
      time: "09:34 PM",
    },
    {
      id: 2,
      nurseName: note.assignedNurseName || "Kaaviya",
      role: "Nurse",
      noteText:
        "Vitals rechecked. BP 140/86, Pulse 82, SpO2 98%. No complaints. Continuing observation.",
      date: "12 Oct 2026",
      time: "10:45 PM",
    },
  ];

  const updates =
    (Array.isArray(note.nursingNotes) &&
      note.nursingNotes.length > 0 &&
      note.nursingNotes) ||
    (Array.isArray(note.nurseUpdates) &&
      note.nurseUpdates.length > 0 &&
      note.nurseUpdates) ||
    (Array.isArray(note.updates) &&
      note.updates.length > 0 &&
      note.updates) ||
    (Array.isArray(note.followUpNotes) &&
      note.followUpNotes.length > 0 &&
      note.followUpNotes) ||
    defaultNursingUpdates;

  return (
    <div className="border border-[#e5eaf2] rounded-2xl p-5 sm:p-6 bg-white shadow-xs hover:shadow-sm transition-all duration-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#f0f7ff] border border-[#d6e7fc] flex items-center justify-center text-[#2563eb] shrink-0">
            <User className="w-5 h-5 text-[#2563eb]" />
          </div>
          <h3 className="text-[16px] font-bold text-[#111827]">
            {doctorName}
          </h3>
          <div className="bg-[#f0edff] text-[#6366f1] px-3 py-1 rounded-full text-[12px] font-semibold flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-[#6366f1]" />
            <span>{noteType}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onView && onView(note)}
            title="View note details"
            className="w-9 h-9 rounded-xl border border-[#e2e8f0] bg-white flex items-center justify-center text-[#0284c7] hover:bg-sky-50 transition cursor-pointer"
          >
            <Eye className="w-4 h-4" />
          </button>
          {/* <button
            onClick={() => onEdit && onEdit(note)}
            title="Edit note"
            className="w-9 h-9 rounded-xl border border-[#e2e8f0] bg-white flex items-center justify-center text-[#0d9488] hover:bg-teal-50 transition cursor-pointer"
          >
            <Pencil className="w-4 h-4" />
          </button> */}
          <button
            onClick={() => setExpanded(!expanded)}
            title={expanded ? "Collapse note" : "Expand note"}
            className="w-9 h-9 rounded-xl border border-[#e2e8f0] bg-white flex items-center justify-center text-[#0284c7] hover:bg-sky-50 transition cursor-pointer"
          >
            {expanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* MAIN NOTE TEXT */}
      <div className="mt-3.5 mb-6">
        <p className="text-[15px] font-semibold text-[#1e293b] leading-relaxed">
          {noteText}
        </p>
      </div>

      {/* TIMELINE (EXPANDED) */}
      {expanded && (
        <div className="relative pl-8 pt-1">
          {/* Continuous vertical teal timeline line */}
          <div className="absolute left-[7px] top-4 bottom-8 w-[2px] bg-[#008f9f]" />

          {/* NODE 1: INSTRUCTION FOR NURSING STAFF */}
          <div className="relative mb-5">
            {/* Node 1 dot */}
            <div className="absolute -left-[31px] top-4 w-3.5 h-3.5 rounded-full bg-[#008f9f] ring-4 ring-white z-10" />

            {/* Instruction Card */}
            <div className="border border-[#dbeafe] bg-[#f8faff] rounded-2xl p-4 sm:p-5">
              <div className="flex items-center gap-2.5 mb-3.5">
                <div className="w-8 h-8 rounded-lg bg-[#dbeafe] text-[#2563eb] flex items-center justify-center shrink-0">
                  <ClipboardCheck className="w-4 h-4 text-[#2563eb]" />
                </div>
                <h4 className="text-[14px] font-bold text-[#0f172a]">
                  Instruction for Nursing Staff
                </h4>
              </div>

              {/* Instruction Content */}
              <div className="bg-white border border-[#e2e8f0] rounded-xl px-4 py-3 text-[13px] text-[#334155] leading-relaxed mb-4">
                {instructionText}
              </div>

              {/* Metadata Row: Due, Task Status, Priority */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Due */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#eff6ff] text-[#2563eb] flex items-center justify-center shrink-0">
                    <Calendar className="w-4 h-4 text-[#2563eb]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium text-[#94a3b8] leading-tight">
                      Due
                    </p>
                    <p className="text-[13px] font-semibold text-[#1e293b] leading-tight mt-0.5 truncate">
                      {formatDueDatetime(dueDateTimeStr)}
                    </p>
                  </div>
                </div>

                {/* Task Status */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#ecfeff] text-[#06b6d4] flex items-center justify-center shrink-0">
                    <Settings className="w-4 h-4 text-[#06b6d4]" />
                  </div>
                  <div>
                    <p className="text-[11px] font-medium text-[#94a3b8] leading-tight">
                      Task Status
                    </p>
                    <span
                      className={`inline-block mt-0.5 px-3 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${getStatusBadgeClass(
                        taskStatus
                      )}`}
                    >
                      {taskStatus}
                    </span>
                  </div>
                </div>

                {/* Priority */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#eff6ff] text-[#3b82f6] flex items-center justify-center shrink-0">
                    <Flag className="w-4 h-4 text-[#3b82f6]" />
                  </div>
                  <div>
                    <p className="text-[11px] font-medium text-[#94a3b8] leading-tight">
                      Priority
                    </p>
                    <span
                      className={`inline-block mt-0.5 px-3.5 py-0.5 rounded-full text-[11px] font-bold capitalize ${getPriorityBadgeClass(
                        priority
                      )}`}
                    >
                      {priority}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* NODE 2: NURSING NOTES (UPDATES) */}
          <div className="relative">
            {/* Node 2 dot */}
            <div className="absolute -left-[31px] top-4 w-3.5 h-3.5 rounded-full bg-[#008f9f] ring-4 ring-white z-10" />

            {/* Nursing Notes Card */}
            <div className="border border-[#ccfbf1] bg-[#f4fbf9] rounded-2xl p-4 sm:p-5">
              <div className="flex items-center gap-2.5 mb-3.5">
                <div className="w-8 h-8 rounded-lg bg-[#ccfbf1] text-[#0d9488] flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4 text-[#0d9488]" />
                </div>
                <h4 className="text-[14px] font-bold text-[#0f172a]">
                  Nursing Notes (Updates)
                </h4>
              </div>

              {/* Sub-timeline for updates */}
              <div className="relative pl-6 space-y-3">
                {/* Connecting vertical line for sub-timeline */}
                <div className="absolute left-[5px] top-4 bottom-5 w-[1.5px] bg-[#94a3b8]/40" />

                {updates.map((update, idx) => {
                  const dt =
                    update.date && update.time
                      ? { date: update.date, time: update.time }
                      : formatNurseNoteDateTime(
                          update.datetime ||
                            update.createdDatetime ||
                            update.date
                        );

                  return (
                    <div key={update.id || idx} className="relative">
                      {/* Sub-timeline Dot */}
                      <div
                        className={`absolute -left-[23px] top-4 w-2.5 h-2.5 rounded-full ${
                          idx === 0 ? "bg-[#008f9f]" : "bg-[#94a3b8]"
                        } ring-2 ring-[#f4fbf9] z-10`}
                      />

                      {/* Update Card */}
                      <div className="bg-white border border-[#e2e8f0] rounded-xl p-3 sm:px-4 sm:py-3 flex items-center justify-between gap-4 shadow-2xs hover:shadow-xs transition">
                        {/* Left: Avatar & Text */}
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-[#e6fffa] border border-[#b2f5ea] text-[#0d9488] flex items-center justify-center shrink-0">
                            <FaUserNurse className="w-4 h-4 text-[#0d9488]" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[13px] font-bold text-[#0f172a]">
                                {update.nurseName ||
                                  update.recordedByName ||
                                  "Kaaviya"}
                              </span>
                              <span className="bg-[#d1fae5] text-[#059669] px-2 py-0.5 rounded-md text-[11px] font-semibold">
                                {update.role || "Nurse"}
                              </span>
                            </div>
                            <p className="text-[13px] text-[#334155] mt-1 leading-normal">
                              {update.noteText ||
                                update.content ||
                                "Vitals recorded."}
                            </p>
                          </div>
                        </div>

                        {/* Right: Date, Time & View Eye Button */}
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-right">
                            <p className="text-[11px] font-semibold text-[#64748b] leading-tight">
                              {dt.date}
                            </p>
                            <p className="text-[11px] font-medium text-[#94a3b8] leading-tight mt-0.5">
                              {dt.time}
                            </p>
                          </div>
                          <button
                            onClick={() =>
                              onView &&
                              onView({ ...note, selectedUpdate: update })
                            }
                            title="View update details"
                            className="w-8 h-8 rounded-lg border border-[#e2e8f0] bg-white flex items-center justify-center text-[#0284c7] hover:bg-sky-50 transition cursor-pointer"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Attachments Section if present */}
          {attachments.length > 0 && (
            <div className="mt-4 p-4 rounded-xl border border-[#e2e8f0] bg-white">
              <p className="text-[12px] font-semibold text-[#374151] mb-2 flex items-center gap-2">
                <FaPaperclip className="text-[#008f9f] text-[12px]" />
                Uploaded Attachments ({attachments.length})
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {attachments.map((file, fIdx) => (
                  <div
                    key={file.attachmentId || fIdx}
                    onClick={() => onView && onView(note)}
                    className="flex items-center justify-between p-2.5 rounded-lg border border-[#edf1f7] bg-[#fafbff] text-xs cursor-pointer hover:border-[#008f9f] hover:bg-teal-50/30 transition"
                  >
                    <div className="flex items-center gap-2 truncate min-w-0">
                      <FaPaperclip className="text-[#6b7280] shrink-0" />
                      <span className="font-semibold text-gray-800 truncate">
                        {file.fileName || `Attachment_${fIdx + 1}`}
                      </span>
                      {file.fileSizeKb && (
                        <span className="text-gray-400 text-[11px] shrink-0">
                          ({file.fileSizeKb} KB)
                        </span>
                      )}
                    </div>
                    <Eye className="w-3.5 h-3.5 text-[#008f9f]" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Notes({ patientId, admissionId, patientDetails }) {
  const [openModal, setOpenModal] = useState(false);
  const [openNurseActionModal, setOpenNurseActionModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [activeNote, setActiveNote] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const sessionLogin = sessionStorage.getItem("login_result");
  const loginResultObject = sessionLogin ? JSON.parse(sessionLogin) : null;

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ClinicalNotesService.getPatientClinicalNotes({
        pageNo: 1,
        pageSize: 10,
        patientId: patientId || undefined,
        recordedBy: loginResultObject?.user?.userId,
        nurseActionRequired: "Y",
        encounterId: admissionId,
        status: "OPEN",
      });
      console.log("ClinicalNotes response:", res?.data);
      const listData = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.content)
        ? res.data.content
        : [];

      // If no notes exist yet in backend response, provide sample note matching the design
      if (listData.length === 0) {
        setNotes([
          {
            id: "sample-1",
            noteId: "sample-1",
            recordedByName: "Hariharan V",
            role: "Doctor Note",
            noteText: "BP is high",
            nurseActionRequired: "Y",
            nurseActionInstructions: "Take vitals every 1 hour.",
            dueDatetime: "2026-10-12 16:10:00",
            status: "OPEN",
            priority: "High",
            nursingNotes: [
              {
                id: 1,
                nurseName: "Kaaviya",
                role: "Nurse",
                noteText:
                  "Vitals taken. BP 150/90, Pulse 88, SpO2 98%. Patient stable.",
                date: "12 Oct 2026",
                time: "09:34 PM",
              },
              {
                id: 2,
                nurseName: "Kaaviya",
                role: "Nurse",
                noteText:
                  "Vitals rechecked. BP 140/86, Pulse 82, SpO2 98%. No complaints. Continuing observation.",
                date: "12 Oct 2026",
                time: "10:45 PM",
              },
            ],
          },
        ]);
      } else {
        setNotes(listData);
      }
    } catch (err) {
      console.error("Failed to load clinical notes in Notes tab:", err);
      setError(err.message || "Failed to load notes");
      // Fallback sample note on network/auth error in dev
      setNotes([
        {
          id: "sample-1",
          noteId: "sample-1",
          recordedByName: "Hariharan V",
          role: "Doctor Note",
          noteText: "BP is high",
          nurseActionRequired: "Y",
          nurseActionInstructions: "Take vitals every 1 hour.",
          dueDatetime: "2026-10-12 16:10:00",
          status: "OPEN",
          priority: "High",
          nursingNotes: [
            {
              id: 1,
              nurseName: "Kaaviya",
              role: "Nurse",
              noteText:
                "Vitals taken. BP 150/90, Pulse 88, SpO2 98%. Patient stable.",
              date: "12 Oct 2026",
              time: "09:34 PM",
            },
            {
              id: 2,
              nurseName: "Kaaviya",
              role: "Nurse",
              noteText:
                "Vitals rechecked. BP 140/86, Pulse 82, SpO2 98%. No complaints. Continuing observation.",
              date: "12 Oct 2026",
              time: "10:45 PM",
            },
          ],
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [patientId, admissionId, loginResultObject?.user?.userId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const filteredNotes = useMemo(() => {
    return notes.filter((n) => {
      // 1. Date Filter
      if (dateFilter) {
        const itemDateStr = n.noteDatetime || n.createdDate || n.datetime || "";
        if (
          !itemDateStr.includes(dateFilter) &&
          formatDateOnly(itemDateStr) !== formatDateOnly(dateFilter)
        ) {
          return false;
        }
      }

      // 2. Search Term
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const text = (n.noteText || n.content || "").toLowerCase();
        const author = (n.createdBy || n.recordedByName || "").toLowerCase();
        const type = (n.type || n.status || n.noteTypeName || "").toLowerCase();
        if (!text.includes(q) && !author.includes(q) && !type.includes(q)) {
          return false;
        }
      }

      return true;
    });
  }, [notes, dateFilter, searchTerm]);

  const sortedNotes = useMemo(() => {
    return [...filteredNotes].sort((a, b) => {
      const timeA =
        new Date(
          (a.noteDatetime || a.createdDate || a.datetime || "").replace(
            " ",
            "T"
          )
        ).getTime() || 0;
      const timeB =
        new Date(
          (b.noteDatetime || b.createdDate || b.datetime || "").replace(
            " ",
            "T"
          )
        ).getTime() || 0;
      return timeB - timeA;
    });
  }, [filteredNotes]);

  const handleEditNote = (note) => {
    setEditingNote(note);
    setOpenNurseActionModal(true);
  };

  const handleAddNewNote = () => {
    setEditingNote(null);
    setOpenNurseActionModal(true);
  };

  return (
    <div className="flex flex-col gap-6 py-5">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-bold text-[#111827]">
            Doctor/Nurse Notes
          </h2>
        </div>

        <div className="flex items-center gap-3">
          {/* Date filter */}
          <div className="flex items-center gap-2 border border-[#e5e7eb] rounded-lg px-3 h-10 bg-white min-w-[160px]">
            <Calendar className="text-[#6b7280] w-4 h-4" />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="bg-transparent outline-none text-[12px] text-[#374151] font-semibold cursor-pointer w-full"
            />
            {dateFilter && (
              <button
                onClick={() => setDateFilter("")}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Add Note Button */}
          <button
            onClick={handleAddNewNote}
            className="h-10 px-5 rounded-lg bg-[#008f9f] text-white text-[13px] font-semibold flex items-center gap-2 hover:bg-[#007a8a] transition-colors shadow-sm shrink-0 cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            Add Note
          </button>
        </div>
      </div>

      {/* CONTENT LIST */}
      <div className="space-y-4">
        {loading && (
          <div className="space-y-4">
            <SkeletonNote />
            <SkeletonNote />
          </div>
        )}

        {!loading && sortedNotes.length === 0 && (
          <div className="bg-white border border-[#e9edf5] rounded-[18px] p-12 text-center shadow-xs">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3 text-gray-400">
              <FaUserMd className="text-lg" />
            </div>
            <p className="text-[14px] font-semibold text-[#374151]">
              No notes found
            </p>
            <p className="text-[12px] text-[#9ca3af] mt-1">
              {searchTerm || dateFilter
                ? "No notes matched your search/filter criteria."
                : "No clinical notes recorded yet for this patient."}
            </p>
          </div>
        )}

        {!loading &&
          sortedNotes.map((note, index) => (
            <DoctorNoteCard
              key={note.id || note.noteId || index}
              note={note}
              onView={setActiveNote}
              onEdit={handleEditNote}
            />
          ))}
      </div>

      {/* Note Details Modal */}
      {activeNote && (
        <NoteDetailsModal
          note={activeNote}
          onClose={() => setActiveNote(null)}
        />
      )}

      {/* Add Note Modal */}
      {openModal && (
        <AddNoteModal
          patientDetails={patientDetails}
          patientId={patientId}
          admissionId={admissionId}
          onSuccess={fetchNotes}
          onClose={() => {
            setOpenModal(false);
          }}
        />
      )}

      {/* Add / Edit Nurse Action Note Modal */}
      {openNurseActionModal && (
        <AddNurseActionNoteModal
          patientDetails={patientDetails}
          patientId={patientId}
          admissionId={admissionId}
          initialData={editingNote}
          onSuccess={() => {
            fetchNotes();
            setEditingNote(null);
          }}
          onClose={() => {
            setOpenNurseActionModal(false);
            setEditingNote(null);
          }}
        />
      )}
    </div>
  );
}

export default Notes;
